import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, Edit3, Archive, ArchiveRestore, Mail, Phone, ShieldCheck, ShieldX,
  Calendar, Send, AlertCircle, ExternalLink, MessageSquare, Trash2, UserCheck, UserX,
} from "lucide-react";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { Button } from "../components/ui/button";
import { CustomerFormModal } from "../components/admin/CustomerFormModal";
import { DeleteCustomerModal } from "../components/admin/DeleteCustomerModal";
import { SmsReplyModal } from "../components/admin/SmsReplyModal";
import { Timeline } from "../components/admin/Timeline";
import { CustomerFinancialSummary } from "../components/admin/settlement/CustomerFinancialSummary";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

const STATUS_BADGE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  failed_permanent: "bg-red-50 text-red-700 border-red-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-blue-50 text-blue-700 border-blue-200",
};

const TABS = ["Profile", "Bookings", "Trial Leads", "Appointments", "Timeline", "Communications"];

export default function AdminCustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Profile");
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [smsModal, setSmsModal] = useState(null);
  const [verifyState, setVerifyState] = useState(null);
  const [verifyOverride, setVerifyOverride] = useState(null); // { channel: 'email'|'sms', verified: bool }

  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [appts, setAppts] = useState([]);
  const [comms, setComms] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customers/${customerId}`, { headers: headers() });
      setDetail(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Customer not found");
      navigate("/admin/customers");
    }
    setLoading(false);
  };

  useEffect(() => { fetchDetail(); /* eslint-disable-next-line */ }, [customerId]);

  const fetchVerifyState = async () => {
    try {
      const r = await axios.get(`${API}/admin/customers/${customerId}/verification-history`, { headers: headers() });
      setVerifyState(r.data);
    } catch {}
  };

  useEffect(() => { fetchVerifyState(); /* eslint-disable-next-line */ }, [customerId]);

  const fetchTabData = async (which) => {
    setTabLoading(true);
    try {
      if (which === "Bookings") {
        const r = await axios.get(`${API}/admin/customers/${customerId}/bookings`, { headers: headers() });
        setBookings(r.data.bookings || []);
      } else if (which === "Trial Leads") {
        const r = await axios.get(`${API}/admin/customers/${customerId}/trial-leads`, { headers: headers() });
        setLeads(r.data.leads || []);
      } else if (which === "Appointments") {
        const r = await axios.get(`${API}/admin/customers/${customerId}/appointments`, { headers: headers() });
        setAppts(r.data.appointments || []);
      } else if (which === "Communications") {
        const r = await axios.get(`${API}/admin/customers/${customerId}/communications`, { headers: headers() });
        setComms(r.data.communications || []);
      }
    } catch (err) {
      toast.error("Failed to load " + which);
    }
    setTabLoading(false);
  };

  useEffect(() => { if (tab !== "Profile") fetchTabData(tab); /* eslint-disable-next-line */ }, [tab]);

  const handleSendPasswordLink = async () => {
    if (!window.confirm("Email a password setup link to this customer?")) return;
    setSendingLink(true);
    try {
      const r = await axios.post(`${API}/admin/customers/${customerId}/send-password-link`, {}, { headers: headers() });
      toast.success("Password setup link sent");
      if (tab === "Communications") fetchTabData("Communications");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send link");
    }
    setSendingLink(false);
  };

  const handleArchive = async () => {
    const action = detail?.customer?.archived ? "restore" : "archive";
    if (!window.confirm(`${action === "archive" ? "Archive" : "Restore"} this customer?`)) return;
    try {
      await axios.post(`${API}/admin/customers/${customerId}/${action}`, {}, { headers: headers() });
      toast.success(`Customer ${action}d`);
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  if (loading || !detail) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
      </AdminLayout>
    );
  }

  const c = detail.customer;

  return (
    <AdminLayout
      title={c.name || c.email}
      subtitle={c.archived ? "Archived" : c.auth_provider || c.created_by || "Customer"}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/admin/customers")} className="border-[#282828]/10" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button variant="outline" onClick={() => setEditing(true)} className="border-[#9A7B4F]/30 text-[#9A7B4F]" data-testid="edit-customer-btn">
            <Edit3 className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button onClick={handleSendPasswordLink} disabled={sendingLink || !c.email} className="bg-[#9A7B4F] hover:bg-[#8B6B3F]" data-testid="send-password-link-btn">
            {sendingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Password Link
          </Button>
          <Button variant="outline" onClick={() => setSmsModal({ to_phone: c.phone, customer_name: c.name, customer_id: c.id })} disabled={!c.phone} className="border-[#9A7B4F]/30 text-[#9A7B4F]" data-testid="send-sms-btn" title={c.phone ? "Send SMS to this customer" : "No phone on file"}>
            <MessageSquare className="w-4 h-4 mr-2" /> Send SMS
          </Button>
          <Button variant="outline" onClick={handleArchive} className="border-[#282828]/10 text-[#676664]" data-testid="archive-customer-btn">
            {c.archived ? <ArchiveRestore className="w-4 h-4 mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
            {c.archived ? "Restore" : "Archive"}
          </Button>
          <Button variant="outline" onClick={() => setShowDelete(true)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-customer-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      }
    >
      {/* Summary */}
      <div className="mb-6"><CustomerFinancialSummary email={c.email} /></div>
      <div className="bg-white border border-[#282828]/8 p-6 mb-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="customer-summary">
        <Summary label="Bookings" value={detail.counts.bookings} />
        <Summary label="Trial Leads" value={detail.counts.trial_leads} />
        <Summary label="Appointments" value={detail.counts.appointments} />
        <Summary label="Total Spend" value={`$${Number(detail.total_spend).toFixed(2)}`} />
      </div>

      {/* Tabs */}
      <div className="bg-white border border-[#282828]/8">
        <div className="flex border-b border-[#282828]/8 overflow-x-auto" data-testid="customer-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-[12px] tracking-wider uppercase font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t
                  ? "border-[#9A7B4F] text-[#282828]"
                  : "border-transparent text-[#676664]/60 hover:text-[#282828]"
              }`}
              data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === "Profile" && (
            <div data-testid="profile-section">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[13px]">
                <Field label="Name" value={c.name} />
                <Field label="Email" value={c.email} icon={Mail} verified={c.email_verified} />
                <Field label="Phone" value={c.phone || "—"} icon={Phone} verified={c.phone_verified} />
                <Field label="Customer ID" value={c.customer_id} mono />
                <Field label="Billing Address" value={c.billing_address || "—"} />
                <Field label="Shipping Address" value={c.shipping_address || "—"} />
                <Field label="Created" value={formatDateTime(c.created_at)} />
                <Field label="Updated" value={formatDateTime(c.updated_at || c.created_at)} />
                {c.notes_admin && (
                  <div className="col-span-full">
                    <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">Internal admin notes</div>
                    <div className="bg-amber-50/60 border border-amber-100 px-3 py-2 text-[12px] text-[#676664] italic">
                      {c.notes_admin}
                    </div>
                  </div>
                )}
              </div>

              {/* Verification override block */}
              <div className="mt-8 pt-6 border-t border-[#282828]/8" data-testid="verification-block">
                <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-3">Admin verification overrides</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <VerificationCard
                    label="Email"
                    target={c.email}
                    state={verifyState?.email}
                    onMark={(verified) => setVerifyOverride({ channel: "email", verified })}
                  />
                  <VerificationCard
                    label="Phone"
                    target={c.phone}
                    state={verifyState?.phone}
                    onMark={(verified) => setVerifyOverride({ channel: "sms", verified })}
                    disabled={!c.phone}
                  />
                </div>
                <p className="text-[10px] text-[#676664]/40 mt-3 italic">
                  Overrides are stored in <code>verified_contacts</code> and recognised by checkout — verified contacts skip OTP for that channel.
                </p>
              </div>
            </div>
          )}

          {tab !== "Profile" && tabLoading && (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
          )}

          {tab === "Bookings" && !tabLoading && (
            <div data-testid="bookings-section">
              {bookings.length === 0 ? (
                <Empty msg="No bookings yet." />
              ) : (
                <Table headers={["ID", "Outfit", "Event Date", "Total", "Status", "Source"]}>
                  {bookings.map((b) => (
                    <tr key={b.id} className="hover:bg-[#FAF8F0]/50">
                      <td className="px-4 py-3 font-mono text-[11px]">{b.id}</td>
                      <td className="px-4 py-3">{b.outfit_name || "—"}</td>
                      <td className="px-4 py-3">{formatDate(b.event_date)}</td>
                      <td className="px-4 py-3 text-right">${Number(b.total_amount || b.rental_cost || 0).toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusBadge status={b.payment_status} /></td>
                      <td className="px-4 py-3 text-[11px] text-[#676664]/60">
                        {b.source || (b.converted_from_trial ? "trial_conversion" : "web")}
                        {b.trial_lead_id && <Link to="/admin/trial-leads" className="ml-1 text-[#9A7B4F] hover:underline">↗ trial</Link>}
                      </td>
                    </tr>
                  ))}
                </Table>
              )}
            </div>
          )}

          {tab === "Trial Leads" && !tabLoading && (
            <div data-testid="leads-section">
              {leads.length === 0 ? <Empty msg="No trial leads." /> : (
                <Table headers={["ID", "Preferred Date", "Status", "Converted", "Created"]}>
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-[#FAF8F0]/50">
                      <td className="px-4 py-3 font-mono text-[11px]">{l.id}</td>
                      <td className="px-4 py-3">{l.preferred_date || "—"} {l.preferred_time || ""}</td>
                      <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                      <td className="px-4 py-3">
                        {l.converted ? (
                          <Link to="/admin/bookings" className="text-emerald-700 text-[11px] inline-flex items-center gap-1">
                            ✓ {l.converted_booking_id} <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : <span className="text-[#676664]/40 text-[11px]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-[#676664]/60">{formatDate(l.created_at)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </div>
          )}

          {tab === "Appointments" && !tabLoading && (
            <div data-testid="appts-section">
              {appts.length === 0 ? <Empty msg="No appointments." /> : (
                <Table headers={["Type", "Date", "Time", "Status", "Notes"]}>
                  {appts.map((a) => (
                    <tr key={a.id} className="hover:bg-[#FAF8F0]/50">
                      <td className="px-4 py-3 capitalize">{a.appointment_type}</td>
                      <td className="px-4 py-3">{formatDate(a.date)}</td>
                      <td className="px-4 py-3">{a.time}</td>
                      <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                      <td className="px-4 py-3 text-[11px] text-[#676664]/70 max-w-xs truncate">{a.notes || "—"}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </div>
          )}

          {tab === "Timeline" && (
            <div data-testid="customer-timeline-section" className="mt-2">
              <Timeline
                customerId={customerId}
                title="Unified Communication & Activity Timeline"
              />
            </div>
          )}

          {tab === "Communications" && !tabLoading && (
            <div data-testid="comms-section">
              {comms.length === 0 ? <Empty msg="No emails or SMS sent to this customer yet." /> : (
                <Table headers={["Type", "Subject / Preview", "Dir.", "Channel", "Recipient / From", "Status", "When", "Related", "Error"]}>
                  {comms.map((m, i) => {
                    const isInbound = m.is_inbound || m.direction === "inbound";
                    const isSms = m.channel === "sms";
                    return (
                      <tr key={m.id || i} className={`hover:bg-[#FAF8F0]/50 ${isInbound ? "bg-sky-50/30" : ""}`} data-testid={`comms-row-${i}`}>
                        <td className="px-4 py-3 text-[11px] font-medium text-[#9A7B4F]">
                          {m.email_type}
                          {isInbound && <div className="text-[9px] text-sky-700 mt-0.5 font-medium uppercase tracking-wider">Customer reply</div>}
                        </td>
                        <td className="px-4 py-3 text-[12px] max-w-xs truncate" title={m.subject || m.message_preview || m.body_text}>
                          {m.subject || m.message_preview || m.body_text || <span className="text-[#676664]/40 italic">—</span>}
                          {!isInbound && !m.body_available && m.email_type && !m.email_type.startsWith("otp_") && m.email_type !== "password_setup_admin" && m.email_type !== "password_reset" && !isSms && (
                            <div className="text-[9px] text-[#676664]/40 mt-0.5">Content was not stored for this older email.</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[10px] uppercase text-[#676664]/60">{m.direction || (isInbound ? "inbound" : "—")}</td>
                        <td className="px-4 py-3 text-[10px] uppercase text-[#676664]/60">{m.channel || (m.to_email ? "email" : "sms")}</td>
                        <td className="px-4 py-3 text-[11px] text-[#676664]/70">{isInbound ? (m.from_phone || "—") : (m.to_email || m.to_phone || "—")}</td>
                        <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                        <td className="px-4 py-3 text-[11px] text-[#676664]/60">{formatDateTime(m.sent_at || m.received_at || m.created_at)}</td>
                        <td className="px-4 py-3 text-[10px] font-mono text-[#676664]/50">{m.related_id || "—"}</td>
                        <td className="px-4 py-3 text-[10px] text-red-600 max-w-xs truncate" title={m.error_message}>
                          {m.error_message ? <span className="inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />{m.error_message.slice(0, 40)}…</span> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </Table>
              )}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <CustomerFormModal
          customer={c}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); fetchDetail(); }}
        />
      )}

      {showDelete && (
        <DeleteCustomerModal
          customer={c}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate("/admin/customers")}
        />
      )}

      {smsModal && (
        <SmsReplyModal
          to={smsModal}
          title="Send SMS to customer"
          template_key="admin_direct_customer_sms"
          onClose={() => setSmsModal(null)}
          onSent={() => { setSmsModal(null); }}
        />
      )}

      {verifyOverride && (
        <VerifyOverrideModal
          customerId={customerId}
          channel={verifyOverride.channel}
          verified={verifyOverride.verified}
          onClose={() => setVerifyOverride(null)}
          onApplied={() => {
            setVerifyOverride(null);
            fetchVerifyState();
            fetchDetail();
          }}
        />
      )}
    </AdminLayout>
  );
}

function VerificationCard({ label, target, state, onMark, disabled }) {
  const verified = state?.verified;
  const byAdmin = state?.verified_by_admin;
  return (
    <div className={`border p-4 ${verified ? "border-emerald-200 bg-emerald-50/40" : "border-[#282828]/10 bg-[#FAF8F0]/40"}`} data-testid={`verify-card-${label.toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#676664]/60 mb-0.5">{label}</div>
          <div className="text-[13px] text-[#282828] font-mono">{target || <span className="italic text-[#676664]/40">none</span>}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 border uppercase tracking-wider ${verified ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-[#282828]/10 text-[#676664]/50"}`}>
          {verified ? "verified" : "not verified"}
        </span>
      </div>
      {verified && (
        <div className="text-[10px] text-[#676664]/60 space-y-0.5 mb-3">
          {byAdmin && <div>✓ Admin-overridden by <span className="font-mono">{state.verified_by || "—"}</span></div>}
          {state.verified_at && <div>at {new Date(state.verified_at).toLocaleString("en-AU")}</div>}
          {state.verification_source && state.verification_source !== "otp" && (
            <div>source: <span className="font-mono">{state.verification_source}</span></div>
          )}
          {state.verification_note && <div className="italic text-amber-700">note: {state.verification_note}</div>}
        </div>
      )}
      <div className="flex gap-2">
        {!verified && (
          <button
            disabled={disabled}
            onClick={() => onMark(true)}
            className="flex-1 text-[10px] uppercase tracking-wider px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-30 inline-flex items-center justify-center gap-1"
            data-testid={`mark-verified-${label.toLowerCase()}`}
          >
            <UserCheck className="w-3 h-3" /> Mark verified
          </button>
        )}
        {verified && (
          <button
            onClick={() => onMark(false)}
            className="flex-1 text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#282828]/15 text-[#676664] hover:bg-[#FAF8F0] inline-flex items-center justify-center gap-1"
            data-testid={`mark-unverified-${label.toLowerCase()}`}
          >
            <UserX className="w-3 h-3" /> Mark unverified
          </button>
        )}
      </div>
    </div>
  );
}

function VerifyOverrideModal({ customerId, channel, verified, onClose, onApplied }) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const presets = ["Verified manually by admin", "Customer verified in person", "OTP issue", "Imported customer"];

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/customers/${customerId}/verify`,
        { channel, verified, note },
        { headers: headers() },
      );
      toast.success(verified ? `${channel === "email" ? "Email" : "Phone"} marked verified` : `Verification revoked`);
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update verification");
    }
    setSubmitting(false);
  };

  const label = channel === "email" ? "email" : "phone";
  const action = verified ? "verify" : "unverify";

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="verify-override-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#282828]/8" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[17px] text-[#282828] mb-3 capitalize" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
          Admin override: {action} {label}
        </h3>
        <p className="text-[12px] text-[#676664]/70 mb-4">
          {verified
            ? `This will mark the customer's ${label} as verified and let them skip the OTP step at checkout.`
            : `This will revoke verification for this ${label}. The customer will be asked for an OTP again at next checkout.`
          }
        </p>

        {verified && (
          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-wider text-[#676664]/60 mb-1">Internal note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none resize-none"
              placeholder="Why are you verifying this?"
              data-testid="verify-note-input"
            />
            <div className="flex flex-wrap gap-1 mt-2">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setNote(p)}
                  className="text-[10px] px-2 py-0.5 border border-[#282828]/10 text-[#676664]/70 hover:bg-[#FAF8F0]"
                  data-testid={`preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-[#282828]/10">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={verified ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-amber-700 hover:bg-amber-800 text-white"}
            data-testid="confirm-verify-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">{label}</div>
      <div className="text-2xl text-[#282828] font-light">{value}</div>
    </div>
  );
}

function Field({ label, value, icon: Icon, verified, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">{label}</div>
      <div className={`text-[#282828] flex items-center gap-2 ${mono ? "font-mono text-[11px]" : "text-[13px]"}`}>
        {Icon && <Icon className="w-3.5 h-3.5 text-[#676664]/40" />}
        {value}
        {verified !== undefined && (
          verified
            ? <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600"><ShieldCheck className="w-3 h-3" /> verified</span>
            : <span className="inline-flex items-center gap-0.5 text-[10px] text-[#676664]/40"><ShieldX className="w-3 h-3" /> unverified</span>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "bg-[#FAF8F0] text-[#676664] border-[#282828]/10";
  return <span className={`text-[9px] px-2 py-0.5 border uppercase tracking-wider ${cls}`}>{status || "—"}</span>;
}

function Empty({ msg }) {
  return <div className="text-center py-10 text-[#676664]/50 text-[13px]"><MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" />{msg}</div>;
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead className="bg-[#FAF8F0] text-[#676664]/60 text-[10px] uppercase tracking-wider">
          <tr>{headers.map((h, i) => <th key={i} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[#282828]/5">{children}</tbody>
      </table>
    </div>
  );
}
